import { FaSearch, FaTimes } from 'react-icons/fa';
import { AppButton } from '../Button/_index';
import { Chip } from '../Kit/_index';
import { FieldDate, FieldSelect } from '../Form/_index';

const BLOCK = 'LStudio__app_filters';

/*
    🔥 BARRA DE FILTROS

    Hoy los filtros viven adentro de TableHeader, así que una galería,
    un kanban o un calendario no los pueden usar. Acá salen sueltos y
    hablan el mismo contrato que ya usa tu tabla:

        { key, label, type, value, options, onChange }

        type: 'text' | 'select' | 'multi' | 'date' | 'range'

    ⚠️ LOS FILTROS ACTIVOS SE MUESTRAN COMO CHIPS

    Un select que dice "Estado: pendiente" se pierde entre otros ocho
    controles. El chip dice qué está filtrado AHORA y se quita de a
    uno. Es la diferencia entre "no hay resultados" y "no hay
    resultados porque dejaste puesto un filtro de hace diez minutos".

    ⚠️ "LIMPIAR TODO" SOLO APARECE SI HAY ALGO QUE LIMPIAR

    Un botón permanentemente deshabilitado ocupa lugar y no informa
    nada.
*/

const IS_ACTIVE = (filter) => {

    const V = filter.value;

    if (Array.isArray(V)) return V.length > 0;

    return V !== null && V !== undefined && V !== '';
};

const onDescribe = (filter) => {

    const V = filter.value;

    if (Array.isArray(V)) {
        const LABELS = V.map((val) => filter.options?.find((o) => o.value === val)?.label ?? val);
        return LABELS.length > 2 ? `${LABELS.length} seleccionados` : LABELS.join(', ');
    }

    return filter.options?.find((o) => o.value === V)?.label ?? String(V);
};

const FilterControl = ({ filter }) => {

    if (filter.type === 'select') {
        return (
            <FieldSelect
                id={`filter_${filter.key}`}
                label={filter.label}
                value={filter.value ?? ''}
                onChange={(event) => filter.onChange(event.target.value || null)}
                options={filter.options ?? []}
                placeholder={filter.placeholder ?? 'Todos'}
                isSearchable={false}
            />
        );
    }

    if (filter.type === 'multi') {
        return (
            <div className={`${BLOCK}__multi`}>
                {
                    (filter.options ?? []).map((o) => {

                        const CURRENT = filter.value ?? [];
                        const IS_ON = CURRENT.includes(o.value);

                        return (
                            <button key={o.value} type="button" className={`${BLOCK}__toggle`}
                                data-on={IS_ON}
                                onClick={() => filter.onChange(
                                    IS_ON ? CURRENT.filter((v) => v !== o.value) : [...CURRENT, o.value],
                                )} >
                                {o.label}
                            </button>
                        );
                    })
                }
            </div>
        );
    }

    if (filter.type === 'date') {
        const RAW = filter.value ? String(filter.value) : '';
        const PARTS = RAW.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const DATE = PARTS ? new Date(Number(PARTS[1]), Number(PARTS[2]) - 1, Number(PARTS[3])) : null;
        const pad = (value) => String(value).padStart(2, '0');
        return (
            <FieldDate
                id={`filter_${filter.key}`}
                label={filter.label}
                value={DATE}
                onChange={(date) => filter.onChange(date ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` : null)}
            />
        );
    }

    return (
        <input type="text" className={`${BLOCK}__input`} value={filter.value ?? ''}
            placeholder={filter.placeholder ?? filter.label}
            onChange={(e) => filter.onChange(e.target.value || null)} />
    );
};

export const FilterBar = ({
    filters = [],
    search = '',
    onSearch = null,
    searchPlaceholder = 'Buscar…',
    onClearAll = null,
    actions = null,
    variant = 'bar',             // bar | stacked
    showChips = true,
}) => {

    const ACTIVE = filters.filter(IS_ACTIVE);

    const HAS_ANY = ACTIVE.length > 0 || (search ?? '').length > 0;

    return (
        <div className={BLOCK} data-variant={variant}>

            <div className={`${BLOCK}__row`}>

                {
                    onSearch && (
                        <div className={`${BLOCK}__search`}>
                            <span className={`${BLOCK}__label`}>Buscar</span>
                            <div className={`${BLOCK}__search_control`}>
                                <FaSearch className={`${BLOCK}__search_icon`} aria-hidden="true" />
                                <input
                                    type="search"
                                    className={`${BLOCK}__input`}
                                    value={search}
                                    placeholder={searchPlaceholder}
                                    onChange={(e) => onSearch(e.target.value)}
                                    aria-label={searchPlaceholder}
                                />
                            </div>
                        </div>
                    )
                }

                {
                    filters.map((filter) => (
                        filter.type === 'select' || filter.type === 'date'
                            ? <div key={filter.key} className={`${BLOCK}__field ${BLOCK}__field--app`}><FilterControl filter={filter} /></div>
                            : (
                                <label key={filter.key} className={`${BLOCK}__field`}>
                                    <span className={`${BLOCK}__label`}>{filter.label}</span>
                                    <FilterControl filter={filter} />
                                </label>
                            )
                    ))
                }

                {actions && <div className={`${BLOCK}__actions`}>{actions}</div>}

            </div>

            {
                showChips && HAS_ANY && (
                    <div className={`${BLOCK}__chips`}>

                        {
                            search && onSearch && (
                                <Chip tone="neutral" onRemove={() => onSearch('')}>
                                    Búsqueda: {search}
                                </Chip>
                            )
                        }

                        {
                            ACTIVE.map((filter) => (
                                <Chip key={filter.key} tone="accent"
                                    onRemove={() => filter.onChange(Array.isArray(filter.value) ? [] : null)} >
                                    {filter.label}: {onDescribe(filter)}
                                </Chip>
                            ))
                        }

                        {
                            onClearAll && (
                                <AppButton variant="ghost" tone="neutral" size="sm"
                                    icon={<FaTimes />} onClick={onClearAll} >
                                    Limpiar todo
                                </AppButton>
                            )
                        }

                    </div>
                )
            }

        </div>
    );
};
